import {
  isSolanaError,
  type SolanaError,
  SOLANA_ERROR__INSTRUCTION_ERROR__BUILTIN_PROGRAMS_MUST_CONSUME_COMPUTE_UNITS,
  SOLANA_ERROR__INSTRUCTION_ERROR__CUSTOM,
  SOLANA_ERROR__INSTRUCTION_ERROR__UNKNOWN,
  SOLANA_ERROR__JSON_RPC__INTERNAL_ERROR,
  SOLANA_ERROR__JSON_RPC__PARSE_ERROR,
  SOLANA_ERROR__JSON_RPC__SERVER_ERROR_BLOCK_CLEANED_UP,
  SOLANA_ERROR__JSON_RPC__SERVER_ERROR_SEND_TRANSACTION_PREFLIGHT_FAILURE,
  SOLANA_ERROR__MALFORMED_JSON_RPC_ERROR,
  SOLANA_ERROR__RPC__TRANSPORT_HTTP_ERROR,
  SOLANA_ERROR__TRANSACTION_ERROR__ALREADY_PROCESSED,
  SOLANA_ERROR__TRANSACTION_ERROR__BLOCKHASH_NOT_FOUND,
  SOLANA_ERROR__TRANSACTION_ERROR__INSUFFICIENT_FUNDS_FOR_FEE,
  SOLANA_ERROR__TRANSACTION_ERROR__INSUFFICIENT_FUNDS_FOR_RENT,
} from '@solana/kit';
import {
  ChainDisconnectedError,
  ExecutionRevertedError,
  FeeCapTooLowError,
  HttpRequestError,
  InsufficientFundsError,
  InternalRpcError,
  LimitExceededRpcError,
  NonceMaxValueError,
  NonceTooHighError,
  NonceTooLowError,
  ProviderDisconnectedError,
  ResourceUnavailableRpcError,
  RpcError,
  TimeoutError,
  TipAboveFeeCapError,
  TransactionTypeNotSupportedError,
} from 'viem';

import { ApiError } from '@/lib/errors/api';

import { collectErrorChain, collectErrorMessages } from '@/utils/errorChain';
import { extractRevertData } from '@/utils/revertData';

// Pull a hex `data` field off a single error-chain node, unwrapping the
// `{ data: { data } }` shape some RPCs nest it in. Covers plain EIP-1193
// wallet/provider objects that viem's BaseError-gated extractRevertData skips.
const revertDataFromNode = (node: unknown): string | undefined => {
  if (node == null || typeof node !== 'object' || !('data' in node)) return undefined;
  const raw = (node as { data: unknown }).data;
  const hex =
    raw != null && typeof raw === 'object' && 'data' in raw ? (raw as { data: unknown }).data : raw;
  return typeof hex === 'string' && hex.startsWith('0x') ? hex : undefined;
};

/** Whether the error chain carries non-empty, decodable EVM revert bytes. */
const hasRevertData = (err: unknown): boolean => {
  const viemData = extractRevertData(err);
  if (viemData && viemData !== '0x') return true;
  return collectErrorChain(err).some((node) => {
    const data = revertDataFromNode(node);
    return !!data && data !== '0x';
  });
};

/**
 * Canonical machine sub-reasons for a submission-stage transaction failure,
 * surfaced via the free-form Amplitude `submission_reason` property — the
 * submission-stage counterpart to `revert_reason` (inclusion-stage). Shared by
 * every lane (standard, gasless, Solana, cross-chain, limit). Keep bounded — it
 * is a low-cardinality dimension.
 */
export const SUBMISSION_REASON = {
  WALLET_CLIENT_UNAVAILABLE: 'wallet_client_unavailable',
  INVALID_TX_TYPE: 'invalid_tx_type',
  MISSING_INPUT: 'missing_input',
  EXECUTION_REVERTED: 'execution_reverted',
  INSUFFICIENT_GAS: 'insufficient_gas',
  FEE_TOO_LOW: 'fee_too_low',
  NONCE_ERROR: 'nonce_error',
  TX_TYPE_UNSUPPORTED: 'tx_type_unsupported',
  IN_FLIGHT_LIMIT: 'in_flight_limit',
  RPC_TIMEOUT: 'rpc_timeout',
  RPC_RATE_LIMITED: 'rpc_rate_limited',
  RPC_INTERNAL: 'rpc_internal',
  RPC_HTTP: 'rpc_http',
  RPC_DISCONNECTED: 'rpc_disconnected',
  RPC_ERROR: 'rpc_error',
  PREFLIGHT_PROGRAM_ERROR: 'preflight_program_error',
  PREFLIGHT_FAILURE: 'preflight_failure',
  BLOCKHASH_EXPIRED: 'blockhash_expired',
  ALREADY_PROCESSED: 'already_processed',
  SOLANA_ERROR: 'solana_error',
  UNKNOWN: 'unknown',
} as const;

export type SubmissionReason = (typeof SUBMISSION_REASON)[keyof typeof SUBMISSION_REASON];

/** Both submission-failure analytics fields, derived from one error in one pass. */
export type SubmissionFailure = {
  submissionReason: string;
  revertReason?: string;
};

type SolanaMatch = {
  reason: SubmissionReason;
  programDetail?: string;
};

const isInstructionError = (node: unknown): boolean => {
  if (!isSolanaError(node)) return false;
  const code = node.context.__code;
  return (
    code >= SOLANA_ERROR__INSTRUCTION_ERROR__UNKNOWN &&
    code <= SOLANA_ERROR__INSTRUCTION_ERROR__BUILTIN_PROGRAMS_MUST_CONSUME_COMPUTE_UNITS
  );
};

const getPreflightInstructionDetail = (node: unknown): string | undefined => {
  if (!isInstructionError(node) || !isSolanaError(node)) return undefined;

  if (isSolanaError(node, SOLANA_ERROR__INSTRUCTION_ERROR__CUSTOM)) {
    return `preflight:custom:${node.context.code}:ix${node.context.index}`;
  }

  const { context } = node;
  const index = 'index' in context ? context.index : 'unknown';
  return `preflight:instruction:${context.__code}:ix${index}`;
};

// Trail of the numeric code on every SolanaError in the chain, e.g.
// `solana:-32002:7050008`. The predicate wraps `isSolanaError` so `filter`'s
// index argument is not read as its second `code` parameter.
const getSolanaErrorCodeTrail = (chain: readonly unknown[]): string | undefined => {
  const codes = chain
    .filter((node): node is SolanaError => isSolanaError(node))
    .map((node) => node.context.__code);
  return codes.length ? `solana:${codes.join(':')}` : undefined;
};

// Match the first SolanaError in the chain whose code maps to a specific
// reason. Returns nothing for a recognised-but-unmapped code so the viem and
// message classifiers still get a chance before the `solana_error` last resort.
const matchSolanaReason = (chain: readonly unknown[]): SolanaMatch | undefined => {
  for (const [index, node] of chain.entries()) {
    if (!isSolanaError(node)) continue;

    if (
      isSolanaError(node, SOLANA_ERROR__JSON_RPC__SERVER_ERROR_SEND_TRANSACTION_PREFLIGHT_FAILURE)
    ) {
      const cause = chain.slice(index + 1).find((candidate) => isSolanaError(candidate));
      if (isSolanaError(cause, SOLANA_ERROR__TRANSACTION_ERROR__BLOCKHASH_NOT_FOUND)) {
        return { reason: SUBMISSION_REASON.BLOCKHASH_EXPIRED };
      }
      if (isSolanaError(cause, SOLANA_ERROR__TRANSACTION_ERROR__ALREADY_PROCESSED)) {
        return { reason: SUBMISSION_REASON.ALREADY_PROCESSED };
      }
      if (
        isSolanaError(cause, SOLANA_ERROR__TRANSACTION_ERROR__INSUFFICIENT_FUNDS_FOR_FEE) ||
        isSolanaError(cause, SOLANA_ERROR__TRANSACTION_ERROR__INSUFFICIENT_FUNDS_FOR_RENT)
      ) {
        return { reason: SUBMISSION_REASON.INSUFFICIENT_GAS };
      }

      // Program errors carry a richer, program-specific detail than the code trail.
      const programDetail = getPreflightInstructionDetail(cause);
      if (programDetail) {
        return { reason: SUBMISSION_REASON.PREFLIGHT_PROGRAM_ERROR, programDetail };
      }
      return { reason: SUBMISSION_REASON.PREFLIGHT_FAILURE };
    }

    if (isSolanaError(node, SOLANA_ERROR__RPC__TRANSPORT_HTTP_ERROR)) {
      return {
        reason:
          node.context.statusCode === 429
            ? SUBMISSION_REASON.RPC_RATE_LIMITED
            : SUBMISSION_REASON.RPC_HTTP,
      };
    }
    if (isSolanaError(node, SOLANA_ERROR__JSON_RPC__INTERNAL_ERROR)) {
      return { reason: SUBMISSION_REASON.RPC_INTERNAL };
    }
    if (isSolanaError(node, SOLANA_ERROR__MALFORMED_JSON_RPC_ERROR)) {
      return { reason: SUBMISSION_REASON.RPC_ERROR };
    }

    const code = node.context.__code;
    if (
      code >= SOLANA_ERROR__JSON_RPC__PARSE_ERROR &&
      code <= SOLANA_ERROR__JSON_RPC__SERVER_ERROR_BLOCK_CLEANED_UP
    ) {
      return { reason: SUBMISSION_REASON.RPC_ERROR };
    }
  }

  return undefined;
};

// Walks the cause chain matching by viem class / EIP-1193 code. RPC subclasses
// all extend RpcError, so the catch-all must stay last.
const classifyByErrorType = (err: unknown): SubmissionReason | undefined => {
  for (const node of collectErrorChain(err)) {
    if (node instanceof ExecutionRevertedError) return SUBMISSION_REASON.EXECUTION_REVERTED;
    if (node instanceof InsufficientFundsError) return SUBMISSION_REASON.INSUFFICIENT_GAS;
    if (node instanceof FeeCapTooLowError || node instanceof TipAboveFeeCapError)
      return SUBMISSION_REASON.FEE_TOO_LOW;
    if (
      node instanceof NonceTooLowError ||
      node instanceof NonceTooHighError ||
      node instanceof NonceMaxValueError
    )
      return SUBMISSION_REASON.NONCE_ERROR;
    if (node instanceof TransactionTypeNotSupportedError)
      return SUBMISSION_REASON.TX_TYPE_UNSUPPORTED;

    if (node instanceof TimeoutError) return SUBMISSION_REASON.RPC_TIMEOUT;
    if (node instanceof HttpRequestError) return SUBMISSION_REASON.RPC_HTTP;
    if (node instanceof LimitExceededRpcError || node instanceof ResourceUnavailableRpcError)
      return SUBMISSION_REASON.RPC_RATE_LIMITED;
    if (node instanceof InternalRpcError) return SUBMISSION_REASON.RPC_INTERNAL;
    if (node instanceof ChainDisconnectedError || node instanceof ProviderDisconnectedError)
      return SUBMISSION_REASON.RPC_DISCONNECTED;

    // EIP-1193 numeric codes — covers both plain `{ code }` objects that wallets
    // throw and viem `RpcError`s carrying a code. Checked before the generic
    // `RpcError` catch-all below so a meaningful code wins over `rpc_error`.
    const code =
      typeof node === 'object' && node !== null && 'code' in node
        ? (node as { code: unknown }).code
        : undefined;
    if (typeof code === 'number') {
      if (code === 3) return SUBMISSION_REASON.EXECUTION_REVERTED;
      if (code === -32005 || code === -32002) return SUBMISSION_REASON.RPC_RATE_LIMITED;
      if (code === -32603) return SUBMISSION_REASON.RPC_INTERNAL;
      if (code === 4900 || code === 4901) return SUBMISSION_REASON.RPC_DISCONNECTED;
    }

    if (node instanceof RpcError) return SUBMISSION_REASON.RPC_ERROR;
  }
  return undefined;
};

// Cross-browser fetch/transport failure messages (request produced no HTTP
// response); shared by the rpc_http rule below and isNetworkError.
const FETCH_FAILURE_MESSAGE = /http request failed|failed to fetch|network ?error|load failed/i;

// Message fallback for non-viem errors (Privy/wallet wrappers, our own
// synthetic errors). Ordered most-specific-first.
const SUBMISSION_REASON_RULES: Array<{
  pattern: RegExp;
  reason: SubmissionReason;
}> = [
  {
    pattern: /wallet client unavailable/i,
    reason: SUBMISSION_REASON.WALLET_CLIENT_UNAVAILABLE,
  },
  // Our own synthetic client-guard messages — kept above the broad rpc/node
  // rules so a controlled message can't be stolen by a looser pattern.
  {
    pattern: /invalid transaction type/i,
    reason: SUBMISSION_REASON.INVALID_TX_TYPE,
  },
  {
    pattern: /failed to submit tx/i,
    reason: SUBMISSION_REASON.MISSING_INPUT,
  },
  {
    pattern: /insufficient funds for gas|total cost.*exceeds the balance/i,
    reason: SUBMISSION_REASON.INSUFFICIENT_GAS,
  },
  {
    pattern: /fee cap.*lower than.*base fee|max fee per gas|transaction underpriced/i,
    reason: SUBMISSION_REASON.FEE_TOO_LOW,
  },
  {
    pattern: /nonce too (low|high)|nonce has already been used/i,
    reason: SUBMISSION_REASON.NONCE_ERROR,
  },
  {
    pattern: /transaction type.*not supported|unsupported transaction type/i,
    reason: SUBMISSION_REASON.TX_TYPE_UNSUPPORTED,
  },
  {
    pattern: /execution reverted|would revert|failed_would_revert/i,
    reason: SUBMISSION_REASON.EXECUTION_REVERTED,
  },
  {
    pattern: /in-flight transaction limit/i,
    reason: SUBMISSION_REASON.IN_FLIGHT_LIMIT,
  },
  {
    pattern: /rate limit|too many requests|exceeds defined limit/i,
    reason: SUBMISSION_REASON.RPC_RATE_LIMITED,
  },
  {
    pattern: /timed out|timeout|took too long/i,
    reason: SUBMISSION_REASON.RPC_TIMEOUT,
  },
  {
    pattern: FETCH_FAILURE_MESSAGE,
    reason: SUBMISSION_REASON.RPC_HTTP,
  },
  {
    pattern: /an internal error was received|internal json-rpc/i,
    reason: SUBMISSION_REASON.RPC_INTERNAL,
  },
  {
    pattern: /json-rpc|rpc error|unknown rpc|unknown connector/i,
    reason: SUBMISSION_REASON.RPC_ERROR,
  },
];

/**
 * Derive both submission-stage analytics fields from one failure: the
 * machine-readable `submission_reason` and, when the chain contains a
 * SolanaError, a `revert_reason` naming the actual Solana code trail (or a
 * richer program-specific detail for preflight program errors).
 *
 * Order: 0x/relayer `ApiError`s expose a stable upstream error key (used by the
 * gasless `relayer_rejected` / `relayer_unavailable` lanes); then specifically
 * mapped Solana errors, viem error class / EIP-1193 code, message rules, the
 * `solana_error` last resort for a recognised-but-unmapped SolanaError, and
 * finally `'unknown'` (non-Solana only).
 * `submissionReason` is `string` because the upstream `ApiError` key is not one
 * of the canonical {@link SUBMISSION_REASON} codes.
 */
export const getSubmissionFailure = (err: unknown): SubmissionFailure => {
  const chain = collectErrorChain(err);
  for (const node of chain) {
    if (node instanceof ApiError) return { submissionReason: node.details.name };
  }

  const codeTrail = getSolanaErrorCodeTrail(chain);

  const solana = matchSolanaReason(chain);
  if (solana) {
    return { submissionReason: solana.reason, revertReason: solana.programDetail ?? codeTrail };
  }

  const byType = classifyByErrorType(err);
  // -32603 masks reverts that still carry decodable bytes; upgrade only that
  // generic code so specific classifications (gas, nonce, fee) stay intact.
  if (byType === SUBMISSION_REASON.RPC_INTERNAL && hasRevertData(err)) {
    return { submissionReason: SUBMISSION_REASON.EXECUTION_REVERTED, revertReason: codeTrail };
  }
  if (byType) return { submissionReason: byType, revertReason: codeTrail };

  const combined = collectErrorMessages(err).join(' ');
  for (const rule of SUBMISSION_REASON_RULES) {
    if (rule.pattern.test(combined)) {
      return { submissionReason: rule.reason, revertReason: codeTrail };
    }
  }

  if (codeTrail) {
    return { submissionReason: SUBMISSION_REASON.SOLANA_ERROR, revertReason: codeTrail };
  }
  return { submissionReason: SUBMISSION_REASON.UNKNOWN };
};

/** The `submission_reason` half of {@link getSubmissionFailure}, for EVM-only lanes. */
export const getSubmissionReason = (err: unknown): string =>
  getSubmissionFailure(err).submissionReason;

/**
 * Whether a submission failure is a client-side network/transport failure (no
 * HTTP response) rather than an upstream 4xx/5xx (MAT-6887). `TypeError` is
 * gated on a fetch-shaped message because ordinary JS bugs throw it too.
 */
export const isNetworkError = (error: Error): boolean =>
  error.name === 'AbortError' ||
  (error.name === 'TypeError' && FETCH_FAILURE_MESSAGE.test(error.message));
